import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AddIcon from '@mui/icons-material/Add';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import { useLocalization } from '../hooks/useLocalization';

const UserBonuses = () => {
  const { t } = useLocalization();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState('');
  const [userBonuses, setUserBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [addBonusDialogOpen, setAddBonusDialogOpen] = useState(false);
  const [newBonus, setNewBonus] = useState({
    calculated_bonus: '',
    bonus_type: 'discount',
    bonus_expiration: ''
  });

  // Helper functions for expiration handling
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    return hoursDiff <= 24 && hoursDiff > 0;
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    return expiration < now;
  };

  const formatExpirationTime = (expirationDate) => {
    if (!expirationDate) return t('never_expires');
    
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return t('expired');
    }
    
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    const hoursDiff = Math.floor((timeDiff % (1000 * 3600 * 24)) / (1000 * 3600));
    
    if (daysDiff > 0) {
      return `${daysDiff} ${t('days')} ${hoursDiff} ${t('hours')}`;
    } else if (hoursDiff > 0) {
      return `${hoursDiff} ${t('hours')}`;
    } else {
      return t('less_than_hour');
    }
  };

  const getExpirationColor = (expirationDate) => {
    if (!expirationDate) return 'success';
    if (isExpired(expirationDate)) return 'error';
    if (isExpiringSoon(expirationDate)) return 'warning';
    return 'info';
  };

  const getExpirationIcon = (expirationDate) => {
    if (!expirationDate) return '♾️';
    if (isExpired(expirationDate)) return '⏰';
    if (isExpiringSoon(expirationDate)) return '⚠️';
    return '📅';
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get('email');
    if (email) {
      setUserEmail(email.trim());
      handleSearch(email.trim());
    }
  }, [location]);

  const handleSearch = async (email = userEmail) => {
    if (!email) {
      setError(t('please_enter_user_email'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/users/bonuses/${encodeURIComponent(email)}`);
      // Filter out points bonuses - only show discount and free_item bonuses
      const filteredBonuses = (response.data.bonuses || []).filter(bonus => bonus.bonus_type !== 'points');
      setUserBonuses(filteredBonuses);
    } catch (err) {
      console.error('Error fetching user bonuses:', err);
      setError(err.response?.data?.detail || t('failed_to_fetch_user_bonuses'));
      setUserBonuses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBonus = async () => {
    try {
      setLoading(true);
      setError('');
      await axios.delete(`/users/bonuses/${encodeURIComponent(userEmail)}/${selectedBonus.attachment_bonus_id}`);
      setDeleteDialogOpen(false);
      handleSearch();
    } catch (err) {
      console.error('Error deleting bonus:', err);
      setError(err.response?.data?.detail || t('failed_to_delete_bonus'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllBonuses = async () => {
    try {
      setLoading(true);
      setError('');
      await axios.delete(`/users/bonuses/${encodeURIComponent(userEmail)}`);
      setDeleteAllDialogOpen(false);
      handleSearch();
    } catch (err) {
      console.error('Error deleting all bonuses:', err);
      setError(err.response?.data?.detail || t('failed_to_delete_all_bonuses'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddBonus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(
        `/users/bonuses/${encodeURIComponent(userEmail)}`,
        {
          ...newBonus,
          calculated_bonus: parseInt(newBonus.calculated_bonus),
          // Don't send expiration date for points bonuses
          bonus_expiration: newBonus.bonus_type === 'points' ? null : (newBonus.bonus_expiration || null)
        }
      );
      setAddBonusDialogOpen(false);
      setNewBonus({
        calculated_bonus: '',
        bonus_type: 'discount',
        bonus_expiration: ''
      });
      handleSearch();
    } catch (err) {
      console.error('Error adding bonus:', err);
      setError(err.response?.data?.detail || t('failed_to_add_bonus'));
    } finally {
      setLoading(false);
    }
  };

  const getBonusTypeColor = (type) => {
    switch (type) {
      case 'points': return 'success';
      case 'discount': return 'primary';
      case 'free_item': return 'secondary';
      default: return 'default';
    }
  };

  const getBonusTypeIcon = (type) => {
    switch (type) {
      case 'points': return <AttachMoneyIcon />;
      case 'discount': return <AutoAwesomeIcon />;
      case 'free_item': return <CardGiftcardIcon />;
      default: return <CardGiftcardIcon />;
    }
  };

  const columns = [
    { 
      field: 'calculated_bonus', 
      headerName: t('bonus_amount_column'), 
      flex: 1,
      minWidth: 120,
      type: 'number',
      renderCell: (params) => {
        const bonusValue = params.value;
        const bonusType = params.row.bonus_type;
        const displayValue = bonusType === 'discount' ? `${bonusValue}%` : 
                           bonusType === 'free_item' ? `${bonusValue}${t('currency')}` : bonusValue;
        
        return (
          <Chip
            icon={<AutoAwesomeIcon />}
            label={displayValue}
            color="success"
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'bonus_type', 
      headerName: t('bonus_type_column'), 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const getLocalizedBonusType = (type) => {
          switch (type) {
            case 'discount':
              return t('bonus_type_discount');
            case 'free_item':
              return t('bonus_type_free_item');
            case 'points':
              return t('bonus_type_points');
            default:
              return type;
          }
        };
        
        return (
          <Chip
            icon={getBonusTypeIcon(params.value)}
            label={getLocalizedBonusType(params.value)}
            color={getBonusTypeColor(params.value)}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'bonus_expiration', 
      headerName: t('expiration_date_column'), 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        if (!params.row.bonus_expiration) {
          return t('never_expires');
        }
        return formatExpirationTime(params.row.bonus_expiration);
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '16px' }}>
            {getExpirationIcon(params.row.bonus_expiration)}
          </span>
          <Chip
            label={params.value}
            color={getExpirationColor(params.row.bonus_expiration)}
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.75rem'
            }}
          />
        </Box>
      )
    },
    { 
      field: 'bonus_status', 
      headerName: t('status'), 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'new_bonus':
              return 'default';
            case 'usage_required_by_user':
              return 'warning';
            case 'used':
              return 'success';
            default:
              return 'default';
          }
        };
        
        const getStatusLabel = (status) => {
          switch (status) {
            case 'new_bonus':
              return t('status_new');
            case 'usage_required_by_user':
              return t('status_pending');
            case 'used':
              return t('status_used');
            default:
              return status;
          }
        };
        
        return (
          <Chip
            label={getStatusLabel(params.value)}
            color={getStatusColor(params.value)}
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'created_at', 
      headerName: t('created_at'), 
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => new Date(params.row.created_at).toLocaleString(),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={t('delete_bonus_tooltip')}>
          <IconButton
            color="error"
            onClick={() => {
              setSelectedBonus(params.row);
              setDeleteDialogOpen(true);
            }}
            sx={{ 
              '&:hover': {
                bgcolor: 'error.light',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const totalBonusAmount = userBonuses.reduce((sum, bonus) => sum + (bonus.calculated_bonus || 0), 0);
  const discountBonuses = userBonuses.filter(bonus => bonus.bonus_type === 'discount').length;
  const freeItemBonuses = userBonuses.filter(bonus => bonus.bonus_type === 'free_item').length;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <CardGiftcardIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('user_bonuses_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('manage_user_bonuses_rewards_offers')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              mr: 2, 
              bgcolor: 'primary.main'
            }}>
              <SearchIcon sx={{ fontSize: 25 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('search_user_bonuses')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('enter_user_email_view_manage_bonuses')}
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label={t('user_email')}
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              fullWidth
              sx={{ 
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              helperText={t('enter_users_email_address')}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={<SearchIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('search')}
            </Button>
            {!error && userEmail && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddBonusDialogOpen(true)}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('add_bonus')}
              </Button>
            )}
            {userBonuses.length > 0 && (
              <Button
                variant="contained"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setDeleteAllDialogOpen(true)}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('delete_all')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {userBonuses.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  mx: 'auto', 
                  mb: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <CardGiftcardIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {userBonuses.length}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {t('total_bonuses')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  mx: 'auto', 
                  mb: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <AttachMoneyIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {discountBonuses}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {t('discount_bonuses')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  mx: 'auto', 
                  mb: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <CardGiftcardIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {freeItemBonuses}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {t('free_item_bonuses')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bonuses Data Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ 
                width: 50, 
                height: 50, 
                mr: 2, 
                bgcolor: 'primary.main'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 25 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {t('user_bonuses')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userEmail ? t('bonuses_for').replace('{email}', userEmail) : t('search_for_user_to_view_bonuses')}
                </Typography>
              </Box>
            </Box>
            
            <Paper sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
              <DataGrid
                rows={userBonuses}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                getRowId={(row) => `${row.attachment_bonus_id}`}
                disableSelectionOnClick
                autoWidth
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: 'text.secondary',
                      fontSize: '1rem',
                      fontWeight: 500
                    }}>
                      {t('no_bonuses_found')}
                    </Box>
                  )
                }}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    flex: 1,
                  },
                  '& .MuiDataGrid-cell': {
                    flex: 1,
                  },
                }}
              />
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Delete Bonus Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('delete_bonus')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1">
            {t('confirm_delete_bonus')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteBonus} 
            color="error" 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e085e7 0%, #e04a5a 100%)',
              }
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Bonuses Dialog */}
      <Dialog 
        open={deleteAllDialogOpen} 
        onClose={() => setDeleteAllDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('delete_all_bonuses')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1">
            {t('confirm_delete_all_bonuses')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteAllDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAllBonuses} 
            color="error" 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e085e7 0%, #e04a5a 100%)',
              }
            }}
          >
            {t('delete_all_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Bonus Dialog */}
      <Dialog 
        open={addBonusDialogOpen} 
        onClose={() => setAddBonusDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_manual_bonus')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 2, minWidth: '400px' }}>
            <TextField
              label={t('bonus_amount')}
              type="number"
              value={newBonus.calculated_bonus}
              onChange={(e) => setNewBonus({ ...newBonus, calculated_bonus: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              helperText={t('enter_bonus_amount')}
            />
            <FormControl fullWidth>
              <InputLabel>{t('bonus_type')}</InputLabel>
              <Select
                value={newBonus.bonus_type}
                label={t('bonus_type')}
                onChange={(e) => {
                  const bonusType = e.target.value;
                  setNewBonus({ 
                    ...newBonus, 
                    bonus_type: bonusType,
                    // Clear expiration date for points bonuses
                    bonus_expiration: bonusType === 'points' ? '' : newBonus.bonus_expiration
                  });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="discount">{t('bonus_type_discount')}</MenuItem>
                <MenuItem value="free_item">{t('bonus_type_free_item')}</MenuItem>
              </Select>
            </FormControl>
            {newBonus.bonus_type !== 'points' && (
              <TextField
                label={t('expiration_date')}
                type="datetime-local"
                value={newBonus.bonus_expiration}
                onChange={(e) => setNewBonus({ ...newBonus, bonus_expiration: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={t('points_bonuses_do_not_expire')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
            {newBonus.bonus_type === 'points' && (
              <TextField
                label={t('expiration_date')}
                value={t('never')}
                disabled
                helperText={t('points_bonuses_do_not_expire')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setAddBonusDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleAddBonus} 
            variant="contained" 
            disabled={!newBonus.calculated_bonus}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3dd870 0%, #2ee8c7 100%)',
              }
            }}
          >
            {t('add_bonus_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserBonuses; 